import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { ProductPurchaseOption, ProductPurchaseType } from 'content/products';
import { CampaignConfig } from './campaigns/types';

export const campaignActive = {
  activeTimezoneOffsets: ['-240', '-330'],
  activeTimes: {
    // india
    '-330': {
      START_TIME: 'Sat Dec 31 2022 23:59:59 GMT+0530',
      END_TIME: 'Tue Jan 10 2023 23:59:59 GMT+0530',
    },
    // uae
    '-240': {
      START_TIME: 'Sat Dec 31 2022 23:59:59 GMT+0400',
      END_TIME: 'Tue Jan 10 2023 23:59:59 GMT+0400',
    },
  },
  invitePageDescription:
    'Measure the impact of food, activity, stress and sleep on your body using glucose biomarkers. Track your metabolic health with Ultrahuman M1.',
  products: {
    m1_ae: {
      product: 'm1_referral_ae',
      defaultSelection: ProductPurchaseType.otp_3_month_ae_ref,
      defaultProductPurchaseOption: ProductPurchaseOption.oneTimePurchase,
    },
    m1: {
      product: 'm1_referral',
      defaultSelection: ProductPurchaseType.otp_4_week_ref,
      defaultProductPurchaseOption: ProductPurchaseOption.oneTimePurchase,
    },
  },
};

export const campaignBtnHighlightProducts = {
  m1_referral_ae: [
    ProductPurchaseType.otp_1_month_ae_ref,
    ProductPurchaseType.otp_3_month_ae_ref,
  ],
  m1_referral: [
    ProductPurchaseType.otp_12_week_ref,
    ProductPurchaseType.otp_4_week_ref,
  ],
};

export const isCampaignActive = () => {
  const timezoneOffset = new Date().getTimezoneOffset();
  if (
    (campaignActive.activeTimezoneOffsets as Array<string>).includes(
      timezoneOffset.toString(),
    )
  ) {
    if (timezoneOffset in campaignActive.activeTimes) {
      const configElement = (campaignActive as Record<string, any>).activeTimes[
        timezoneOffset
      ];
      const START_TIME = new Date(configElement.START_TIME);
      const END_TIME = new Date(configElement.END_TIME);
      return new Date() > START_TIME && new Date() < END_TIME;
    }
  }
  return false;
};

type ReferralCodes = {
  [store in ShopifyStore]?: { discountCode: string; discount: number };
};

export const referralCodes: ReferralCodes = {
  [ShopifyStore.IN]: { discountCode: 'REFERRAL1000', discount: 1000 },
  [ShopifyStore.AE]: { discountCode: 'REFERRAL100', discount: 100 },
};

export const ringReferralCodes = {
  [ShopifyStore.IN]: { discountCode: 'REFERRINGAIR', discount: 2849.9 },
  [ShopifyStore.AE]: { discountCode: 'REFERRINGAIR', discount: 129.9 },
  [ShopifyStore.US]: { discountCode: 'REFERRINGAIR', discount: 34.9 },
  [ShopifyStore.SA]: { discountCode: 'REFERRINGAIR', discount: 164.5 },
  [ShopifyStore.MX]: { discountCode: 'REFERRINGAIR', discount: 34.9 },
  [ShopifyStore.ROW]: { discountCode: 'REFERRINGAIR', discount: 34.9 },
  [ShopifyStore.GB]: { discountCode: 'REFERRINGAIR', discount: 27.9 },
  [ShopifyStore.EU]: { discountCode: 'REFERRINGAIR', discount: 27.9 },
  // FIXME: Pricing updates needed
  [ShopifyStore.AU]: { discountCode: 'REFERRINGAIR', discount: 27.9 },
  [ShopifyStore.CA]: { discountCode: 'REFERRINGAIR', discount: 34.9 },
  [ShopifyStore.ZA]: { discountCode: 'REFERRINGAIR', discount: 799.9 },
  [ShopifyStore.JP]: { discountCode: 'REFERRINGAIR', discount: 5980 },
};

export const ringProReferralCodes: ReferralCodes = {
  [ShopifyStore.IN]: { discountCode: 'REFERRINGPRO', discount: 4499.9 },
  [ShopifyStore.AE]: { discountCode: 'REFERRINGPRO', discount: 175.9 },
  [ShopifyStore.US]: { discountCode: 'REFERRINGPRO', discount: 47.9 },
  [ShopifyStore.SA]: { discountCode: 'REFERRINGPRO', discount: 206.9 },
  [ShopifyStore.MX]: { discountCode: 'REFERRINGPRO', discount: 987.9 },
  [ShopifyStore.ROW]: { discountCode: 'REFERRINGPRO', discount: 47.9 },
  [ShopifyStore.GB]: { discountCode: 'REFERRINGPRO', discount: 42.9 },
  [ShopifyStore.EU]: { discountCode: 'REFERRINGPRO', discount: 49.9 },
  [ShopifyStore.AU]: { discountCode: 'REFERRINGPRO', discount: 76.9 },
  [ShopifyStore.CA]: { discountCode: 'REFERRINGPRO', discount: 65.9 },
  [ShopifyStore.ZA]: { discountCode: 'REFERRINGPRO', discount: 926.9 },
  [ShopifyStore.JP]: { discountCode: 'REFERRINGPRO', discount: 7780 },
};

export const ringPromoDiscount: Partial<
  Record<
    ShopifyStore,
    {
      discount: number;
      discountType: 'flat' | 'percentage';
      active: boolean;
      discountCode: string;
    }
  >
> = {
  // [ShopifyStore.US]: {
  //   discountCode: 'Q5A4VFTQ9A3C',
  //   discount: 50,
  //   discountType: 'flat',
  //   active: false,
  // },
  // [ShopifyStore.AE]: {
  //   discountCode: 'FQYFHMZNH41T',
  //   discount: 20,
  //   discountType: 'percentage',
  //   active: true,
  // },
  // [ShopifyStore.SA]: {
  //   discountCode: '9MFASACX3A80',
  //   discount: 20,
  //   discountType: 'percentage',
  //   active: true,
  // },
  // [ShopifyStore.IN]: {
  //   discountCode: 'ZJ8C1E7BK6KG',
  //   discount: 15,
  //   discountType: 'percentage',
  //   active: false,
  // },
};

/**
 * M1 Promo Discount Configuration
 * Per-product discount approach: Each M1 product added to cart will include this discount code
 * Shopify will apply the discount to each qualifying M1 product individually
 */
export const m1PromoDiscount: Partial<
  Record<
    ShopifyStore,
    {
      discount: number;
      discountType: 'flat' | 'percentage';
      active: boolean;
      discountCode: string;
    }
  >
> = {
  [ShopifyStore.AE]: {
    discountCode: 'BFSM120',
    discount: 20,
    discountType: 'percentage',
    active: false,
  },
  [ShopifyStore.GB]: {
    discountCode: 'BFSM130',
    discount: 30,
    discountType: 'percentage',
    active: false,
  },
  [ShopifyStore.IN]: {
    discountCode: 'BFSM120',
    discount: 20,
    discountType: 'percentage',
    active: false,
  },
  [ShopifyStore.EU]: {
    discountCode: 'BFSM130',
    discount: 30,
    discountType: 'percentage',
    active: false,
  },
};

export type HomePromoDiscountConfig = {
  discount: number;
  discountType: 'flat' | 'percentage';
  active: boolean;
  discountCode: string;
};

export type HomePromoDiscountOverride = Partial<HomePromoDiscountConfig> & {
  store?: ShopifyStore;
};

export type RingPromoDiscountConfig = {
  discount: number;
  discountType: 'flat' | 'percentage';
  active: boolean;
  discountCode: string;
};

export type RingPromoDiscountOverride = Partial<RingPromoDiscountConfig> & {
  store?: ShopifyStore;
};

export type M1PromoDiscountConfig = {
  discount: number;
  discountType: 'flat' | 'percentage';
  active: boolean;
  discountCode: string;
};

export type M1PromoDiscountOverride = Partial<M1PromoDiscountConfig> & {
  store?: ShopifyStore;
};

export const homePromoDiscount: Partial<
  Record<ShopifyStore, HomePromoDiscountConfig>
> = {
  // [ShopifyStore.US]: {
  //   discountCode: 'DDEB1T988AEK',
  //   discount: 15,
  //   discountType: 'percentage',
  //   active: false,
  // },
  // [ShopifyStore.GB]: {
  //   discountCode: 'VS1GZDNVFWZH',
  //   discount: 15,
  //   discountType: 'percentage',
  //   active: false,
  // },
  // [ShopifyStore.CA]: {
  //   discountCode: 'KW707AJSFXCZ',
  //   discount: 15,
  //   discountType: 'percentage',
  //   active: false,
  // },
  // [ShopifyStore.AU]: {
  //   discountCode: 'BFSHOME25',
  //   discount: 29,
  //   discountType: 'percentage',
  //   active: true,
  // },
};

export const homePromoDiscountByCountry: Partial<
  Record<string, HomePromoDiscountOverride>
> = {
  DE: {
    store: ShopifyStore.EU,
    discountCode: 'VS1GZDNVFWZH',
    discount: 15,
    discountType: 'percentage',
    active: false,
  },
};

export const ringPromoDiscountByCountry: Partial<
  Record<string, RingPromoDiscountOverride>
> = {};

export const m1PromoDiscountByCountry: Partial<
  Record<string, M1PromoDiscountOverride>
> = {};

export const getHomePromoDiscountConfig = ({
  store,
  country,
}: {
  store?: ShopifyStore | null;
  country?: string | null;
}): HomePromoDiscountConfig | undefined => {
  const normalizedCountry = country?.toUpperCase();
  const override = normalizedCountry
    ? homePromoDiscountByCountry[normalizedCountry]
    : undefined;

  const overrideStore = override?.store;
  const resolvedStore = overrideStore ?? store ?? undefined;
  const baseConfig = resolvedStore
    ? homePromoDiscount[resolvedStore]
    : undefined;

  if (!override) {
    return baseConfig;
  }

  if (!baseConfig && !overrideStore) {
    const { store: _ignoredStore, ...rest } = override;
    if (
      rest.discount !== undefined &&
      rest.discountCode !== undefined &&
      rest.discountType !== undefined &&
      rest.active !== undefined
    ) {
      return rest as HomePromoDiscountConfig;
    }
    return undefined;
  }

  const mergedConfig = {
    ...baseConfig,
    ...override,
  } as HomePromoDiscountOverride;
  const { store: _store, ...configWithoutStore } = mergedConfig;

  if (
    configWithoutStore.discount === undefined ||
    configWithoutStore.discountCode === undefined ||
    configWithoutStore.discountType === undefined ||
    configWithoutStore.active === undefined
  ) {
    return baseConfig;
  }

  return configWithoutStore as HomePromoDiscountConfig;
};

export const getRingPromoDiscountConfig = ({
  store,
  country,
}: {
  store?: ShopifyStore | null;
  country?: string | null;
}): RingPromoDiscountConfig | undefined => {
  const normalizedCountry = country?.toUpperCase();
  const override = normalizedCountry
    ? ringPromoDiscountByCountry[normalizedCountry]
    : undefined;

  const overrideStore = override?.store;
  const resolvedStore = overrideStore ?? store ?? undefined;
  const baseConfig = resolvedStore
    ? ringPromoDiscount[resolvedStore]
    : undefined;

  if (!override) {
    return baseConfig;
  }

  if (!baseConfig && !overrideStore) {
    const { store: _ignoredStore, ...rest } = override;
    if (
      rest.discount !== undefined &&
      rest.discountCode !== undefined &&
      rest.discountType !== undefined &&
      rest.active !== undefined
    ) {
      return rest as RingPromoDiscountConfig;
    }
    return undefined;
  }

  const mergedConfig = {
    ...baseConfig,
    ...override,
  } as RingPromoDiscountOverride;
  const { store: _store, ...configWithoutStore } = mergedConfig;

  if (
    configWithoutStore.discount === undefined ||
    configWithoutStore.discountCode === undefined ||
    configWithoutStore.discountType === undefined ||
    configWithoutStore.active === undefined
  ) {
    return baseConfig;
  }

  return configWithoutStore as RingPromoDiscountConfig;
};

export const getM1PromoDiscountConfig = ({
  store,
  country,
}: {
  store?: ShopifyStore | null;
  country?: string | null;
}): M1PromoDiscountConfig | undefined => {
  const normalizedCountry = country?.toUpperCase();
  const override = normalizedCountry
    ? m1PromoDiscountByCountry[normalizedCountry]
    : undefined;

  const overrideStore = override?.store;
  const resolvedStore = overrideStore ?? store ?? undefined;
  const baseConfig = resolvedStore
    ? m1PromoDiscount[resolvedStore]
    : undefined;

  if (!override) {
    return baseConfig;
  }

  if (!baseConfig && !overrideStore) {
    const { store: _ignoredStore, ...rest } = override;
    if (
      rest.discount !== undefined &&
      rest.discountCode !== undefined &&
      rest.discountType !== undefined &&
      rest.active !== undefined
    ) {
      return rest as M1PromoDiscountConfig;
    }
    return undefined;
  }

  const mergedConfig = {
    ...baseConfig,
    ...override,
  } as M1PromoDiscountOverride;
  const { store: _store, ...configWithoutStore } = mergedConfig;

  if (
    configWithoutStore.discount === undefined ||
    configWithoutStore.discountCode === undefined ||
    configWithoutStore.discountType === undefined ||
    configWithoutStore.active === undefined
  ) {
    return baseConfig;
  }

  return configWithoutStore as M1PromoDiscountConfig;
};

export const getCampaignPromoDiscountConfig = ({
  store,
  country,
  product,
  campaignConfig,
}: {
  store?: ShopifyStore | null;
  country?: string | null;
  product: 'ring' | 'ring-pro' | 'home' | 'm1';
  campaignConfig?: CampaignConfig;
}): RingPromoDiscountConfig | HomePromoDiscountConfig | M1PromoDiscountConfig | undefined => {
  // If campaign config is provided and active, return it
  if (campaignConfig && campaignConfig.active) {
    return {
      discountCode: campaignConfig.discountCode,
      discount: campaignConfig.discount,
      discountType: campaignConfig.discountType,
      active: true,
    };
  }

  // Fall back to existing static configurations
  if (product === 'ring' || product === 'ring-pro') {
    return getRingPromoDiscountConfig({ store, country });
  } else if (product === 'home') {
    return getHomePromoDiscountConfig({ store, country });
  } else if (product === 'm1') {
    return getM1PromoDiscountConfig({ store, country });
  }

  return undefined;
};
